import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { PDFDocument, rgb } from 'pdf-lib';
import type { InvoiceAttachment } from '@lib/api/invoices';

/**
 * Generate a PDF from the invoice HTML element, then append attachment pages.
 * - Images (jpeg/png): embedded full-page, scaled to fit
 * - WEBP: converted to PNG via canvas, then embedded
 * - PDF: pages merged
 * - Other: placeholder page with filename
 */
export async function generateInvoiceWithAttachments(
  element: HTMLElement,
  filename: string,
  attachments: InvoiceAttachment[] = []
): Promise<void> {
  // Step 1: Generate base invoice PDF using existing html2canvas + jsPDF approach
  const baseBytes = await generateBaseInvoicePdfBytes(element);

  // If no attachments, just download the base PDF
  if (attachments.length === 0) {
    downloadBlob(new Blob([baseBytes], { type: 'application/pdf' }), filename);
    return;
  }

  // Step 2: Load into pdf-lib
  const pdfDoc = await PDFDocument.load(baseBytes);

  // Step 3: Process each attachment (sorted by display_order already)
  for (const att of attachments) {
    try {
      const fileType = att.file_type.toLowerCase();

      if (fileType === 'application/pdf') {
        await appendPdfAttachment(pdfDoc, att.url);
      } else if (fileType === 'image/jpeg' || fileType === 'image/jpg') {
        await appendImageAttachment(pdfDoc, att.url, 'jpeg');
      } else if (fileType === 'image/png') {
        await appendImageAttachment(pdfDoc, att.url, 'png');
      } else if (fileType === 'image/webp') {
        await appendWebpAttachment(pdfDoc, att.url);
      } else {
        // Office docs or other types — placeholder page
        appendPlaceholderPage(pdfDoc, att.label || att.file_name);
      }
    } catch (err) {
      console.error(`Failed to embed attachment "${att.file_name}":`, err);
      // Add a placeholder page indicating the error
      appendPlaceholderPage(pdfDoc, `${att.label || att.file_name} (failed to embed)`);
    }
  }

  // Step 4: Save and download
  const finalBytes = await pdfDoc.save();
  downloadBlob(new Blob([finalBytes.buffer as ArrayBuffer], { type: 'application/pdf' }), filename);
}

/**
 * Generate the base invoice PDF as an ArrayBuffer using html2canvas + jsPDF
 */
async function generateBaseInvoicePdfBytes(element: HTMLElement): Promise<ArrayBuffer> {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '0';
  container.style.top = '0';
  container.style.width = '8.5in';
  container.style.backgroundColor = '#ffffff';
  container.style.zIndex = '-9999';

  const clone = element.cloneNode(true) as HTMLElement;
  clone.style.backgroundColor = '#ffffff';
  container.appendChild(clone);
  document.body.appendChild(container);

  const canvas = await html2canvas(clone, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    windowWidth: 816,
  });

  document.body.removeChild(container);

  const imgWidth = 210; // A4 width mm
  const pageHeight = 297; // A4 height mm
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  const pdf = new jsPDF('p', 'mm', 'a4');
  const imgData = canvas.toDataURL('image/png');

  let heightLeft = imgHeight;
  let position = 0;

  pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;

  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  return pdf.output('arraybuffer');
}

/**
 * Append a PDF attachment by loading it and copying all pages
 */
async function appendPdfAttachment(pdfDoc: PDFDocument, url: string): Promise<void> {
  const response = await fetch(url);
  const bytes = await response.arrayBuffer();
  const srcDoc = await PDFDocument.load(bytes);
  const pages = await pdfDoc.copyPages(srcDoc, srcDoc.getPageIndices());
  for (const page of pages) {
    pdfDoc.addPage(page);
  }
}

/**
 * Append an image (jpeg or png) as a full page, scaled to fit with margins
 */
async function appendImageAttachment(
  pdfDoc: PDFDocument,
  url: string,
  format: 'jpeg' | 'png'
): Promise<void> {
  const response = await fetch(url);
  const bytes = await response.arrayBuffer();

  const image = format === 'jpeg'
    ? await pdfDoc.embedJpg(bytes)
    : await pdfDoc.embedPng(bytes);

  const imgWidth = image.width;
  const imgHeight = image.height;

  // A4 in points: 595.28 x 841.89
  const pageWidth = 595.28;
  const pageHeight = 841.89;
  const margin = 36; // 0.5 inch margin

  const availW = pageWidth - margin * 2;
  const availH = pageHeight - margin * 2;

  const scale = Math.min(availW / imgWidth, availH / imgHeight, 1);
  const drawW = imgWidth * scale;
  const drawH = imgHeight * scale;

  // Center on page
  const x = (pageWidth - drawW) / 2;
  const y = (pageHeight - drawH) / 2;

  const page = pdfDoc.addPage([pageWidth, pageHeight]);
  page.drawImage(image, { x, y, width: drawW, height: drawH });
}

/**
 * Convert WEBP to PNG via canvas, then embed
 */
async function appendWebpAttachment(pdfDoc: PDFDocument, url: string): Promise<void> {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = url;
  });

  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0);

  const pngDataUrl = canvas.toDataURL('image/png');
  const pngBase64 = pngDataUrl.split(',')[1];
  const pngBytes = Uint8Array.from(atob(pngBase64), c => c.charCodeAt(0));

  const image = await pdfDoc.embedPng(pngBytes);
  const imgWidth = image.width;
  const imgHeight = image.height;

  const pageWidth = 595.28;
  const pageHeight = 841.89;
  const margin = 36;

  const availW = pageWidth - margin * 2;
  const availH = pageHeight - margin * 2;

  const scale = Math.min(availW / imgWidth, availH / imgHeight, 1);
  const drawW = imgWidth * scale;
  const drawH = imgHeight * scale;

  const x = (pageWidth - drawW) / 2;
  const y = (pageHeight - drawH) / 2;

  const page = pdfDoc.addPage([pageWidth, pageHeight]);
  page.drawImage(image, { x, y, width: drawW, height: drawH });
}

/**
 * Add a placeholder page with centered filename text
 */
function appendPlaceholderPage(pdfDoc: PDFDocument, text: string): void {
  const pageWidth = 595.28;
  const pageHeight = 841.89;
  const page = pdfDoc.addPage([pageWidth, pageHeight]);

  const fontSize = 14;
  const textWidth = text.length * fontSize * 0.5; // rough estimate
  const x = Math.max((pageWidth - textWidth) / 2, 36);
  const y = pageHeight / 2;

  page.drawText(text, {
    x,
    y,
    size: fontSize,
    color: rgb(0.3, 0.3, 0.3),
  });

  // Draw an icon-like placeholder
  page.drawText('Attachment', {
    x: (pageWidth - 80) / 2,
    y: y + 30,
    size: 10,
    color: rgb(0.5, 0.5, 0.5),
  });
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
